import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = "/orbispmo/api";

function buildAuthHeaders(extra = {}) {
    const token = localStorage.getItem("cani_auth") || "";
    return {
        ...extra,
        Authorization: `Bearer ${token}`,
        "X-CANI-AUTH": token,
    };
}

function ActionButton({
    children,
    onClick,
    type = "button",
    bg = "#111827",
    color = "#fff",
    border = "1px solid #111827",
    disabled = false,
}) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            style={{
                padding: "10px 14px",
                borderRadius: 10,
                border,
                background: disabled ? "#9ca3af" : bg,
                color,
                fontWeight: 600,
                cursor: disabled ? "not-allowed" : "pointer",
                marginRight: 8,
                marginBottom: 8,
            }}
        >
            {children}
        </button>
    );
}

function Input({ label, value, onChange, ...props }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
            <input
                value={value ?? ""}
                onChange={onChange}
                {...props}
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    outline: "none",
                    fontSize: 14,
                    boxSizing: "border-box",
                }}
            />
        </div>
    );
}

function Select({ label, value, onChange, children }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>{label}</div>
            <select
                value={value ?? ""}
                onChange={onChange}
                style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    outline: "none",
                    fontSize: 14,
                    boxSizing: "border-box",
                    background: "#fff",
                }}
            >
                {children}
            </select>
        </div>
    );
}

async function fetchJsonSafe(url, options = {}) {
    const response = await fetch(url, options);
    const raw = await response.text();

    let data = null;
    try {
        data = raw ? JSON.parse(raw) : null;
    } catch {
        throw new Error(`Respuesta inválida del backend: ${raw || "[vacío]"}`);
    }

    if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.detail || "No se pudo completar la acción.");
    }

    return data;
}

export default function DuplicateProjectModal({ open, onClose, project, onDuplicated }) {
    const navigate = useNavigate();

    const [form, setForm] = useState({
        new_name: "",
        new_code: "",
        account_id: "",
        opportunity_id: "",
        project_manager_user_id: "",
        sponsor_user_id: "",
        planned_start_date: "",
        planned_end_date: "",
        copy_tasks: true,
        copy_risks: true,
        copy_deliverables: true,
        copy_scope: true,
        copy_documents: false,
        open_after_duplicate: true,
    });

    const [catalogs, setCatalogs] = useState({
        accounts: [],
        opportunities: [],
        users: [],
    });

    const [loadingCatalogs, setLoadingCatalogs] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const defaultCode = useMemo(() => {
        const baseCode = String(project?.code || "").trim();
        if (!baseCode) return "";
        return `${baseCode}-COPIA`;
    }, [project]);

    useEffect(() => {
        if (!open || !project) return;

        setForm({
            new_name: project?.name ? `${project.name} · Copia` : "",
            new_code: defaultCode,
            account_id: project?.account_id ? String(project.account_id) : "",
            opportunity_id: project?.opportunity_id ? String(project.opportunity_id) : "",
            project_manager_user_id: project?.project_manager_user_id ? String(project.project_manager_user_id) : "",
            sponsor_user_id: project?.sponsor_user_id ? String(project.sponsor_user_id) : "",
            planned_start_date: project?.planned_start_date || "",
            planned_end_date: project?.planned_end_date || "",
            copy_tasks: true,
            copy_risks: true,
            copy_deliverables: true,
            copy_scope: true,
            copy_documents: false,
            open_after_duplicate: true,
        });
        setError("");
        setSaving(false);
    }, [open, project, defaultCode]);

    useEffect(() => {
        if (!open) return;

        const loadCatalogs = async () => {
            try {
                setLoadingCatalogs(true);
                const [accountsRes, opportunitiesRes, usersRes] = await Promise.all([
                    fetchJsonSafe(`${API_BASE}/accounts_list.php`, { headers: buildAuthHeaders() }),
                    fetchJsonSafe(`${API_BASE}/opportunities_list.php`, { headers: buildAuthHeaders() }),
                    fetchJsonSafe(`${API_BASE}/catalog_users_list.php`, { headers: buildAuthHeaders() }),
                ]);

                setCatalogs({
                    accounts: Array.isArray(accountsRes?.data) ? accountsRes.data : (Array.isArray(accountsRes) ? accountsRes : []),
                    opportunities: Array.isArray(opportunitiesRes?.data) ? opportunitiesRes.data : (Array.isArray(opportunitiesRes) ? opportunitiesRes : []),
                    users: Array.isArray(usersRes?.data) ? usersRes.data : [],
                });
            } catch (err) {
                console.error(err);
                setError(err.message || "No se pudieron cargar los catálogos del duplicado.");
            } finally {
                setLoadingCatalogs(false);
            }
        };

        loadCatalogs();
    }, [open]);

    if (!open) return null;

    const handleCheckbox = (key) => (e) => {
        const checked = e.target.checked;
        setForm((prev) => ({ ...prev, [key]: checked }));
    };

    const handleChange = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setError("");

            if (!project?.id) {
                throw new Error("No se encontró el proyecto origen.");
            }

            if (!String(form.new_name || "").trim()) {
                throw new Error("El nombre de la copia es obligatorio.");
            }

            const data = await fetchJsonSafe(`${API_BASE}/project_duplicate.php`, {
                method: "POST",
                headers: buildAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({
                    source_project_id: Number(project.id),
                    new_name: String(form.new_name || "").trim(),
                    new_code: String(form.new_code || "").trim() || null,
                    account_id: String(form.account_id || "").trim() ? Number(form.account_id) : null,
                    opportunity_id: String(form.opportunity_id || "").trim() ? Number(form.opportunity_id) : null,
                    project_manager_user_id: String(form.project_manager_user_id || "").trim() ? Number(form.project_manager_user_id) : null,
                    sponsor_user_id: String(form.sponsor_user_id || "").trim() ? Number(form.sponsor_user_id) : null,
                    planned_start_date: String(form.planned_start_date || "").trim() || null,
                    planned_end_date: String(form.planned_end_date || "").trim() || null,
                    copy_tasks: !!form.copy_tasks,
                    copy_risks: !!form.copy_risks,
                    copy_deliverables: !!form.copy_deliverables,
                    copy_scope: !!form.copy_scope,
                    copy_documents: !!form.copy_documents,
                }),
            });

            if (typeof onDuplicated === "function") {
                await onDuplicated(data);
            }

            if (form.open_after_duplicate && data?.project_id) {
                navigate(`/projects/${data.project_id}`);
                return;
            }

            if (typeof onClose === "function") onClose();
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo duplicar el proyecto.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.45)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: 920,
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        padding: "18px 20px",
                        borderBottom: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div>
                        <h3 style={{ margin: 0, fontSize: 20, color: "#111827" }}>Duplicar proyecto</h3>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                            Crea una copia y ajusta sus datos antes de generarla.
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer", lineHeight: 1 }}
                    >
                        ×
                    </button>
                </div>

                <div style={{ padding: 20, maxHeight: "80vh", overflowY: "auto" }}>
                    <div
                        style={{
                            background: "#f8fafc",
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 14,
                            marginBottom: 18,
                        }}
                    >
                        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Proyecto origen</div>
                        <div style={{ fontWeight: 700, color: "#111827" }}>{project?.name || "—"}</div>
                        <div style={{ fontSize: 13, color: "#374151", marginTop: 6 }}>
                            Código: <strong>{project?.code || "—"}</strong>
                        </div>
                    </div>

                    {error ? (
                        <div
                            style={{
                                background: "#fee2e2",
                                color: "#991b1b",
                                padding: 12,
                                borderRadius: 10,
                                marginBottom: 16,
                            }}
                        >
                            {error}
                        </div>
                    ) : null}

                    <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 14 }}>
                        <Input
                            label="Nombre de la copia"
                            value={form.new_name}
                            onChange={handleChange("new_name")}
                            placeholder="Proyecto X · Copia"
                        />
                        <Input
                            label="Código"
                            value={form.new_code}
                            onChange={handleChange("new_code")}
                            placeholder="PMO-001-COPIA"
                        />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <Select label="Cuenta" value={form.account_id} onChange={handleChange("account_id")}>
                            <option value="">Sin cuenta</option>
                            {catalogs.accounts.map((a) => (
                                <option key={a.id} value={a.id}>{a.name || a.account_name || `Cuenta ${a.id}`}</option>
                            ))}
                        </Select>

                        <Select label="Oportunidad" value={form.opportunity_id} onChange={handleChange("opportunity_id")}>
                            <option value="">Sin oportunidad</option>
                            {catalogs.opportunities.map((o) => (
                                <option key={o.id} value={o.id}>{o.name || o.opportunity_name || `Oportunidad ${o.id}`}</option>
                            ))}
                        </Select>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <Select label="Project Manager" value={form.project_manager_user_id} onChange={handleChange("project_manager_user_id")}>
                            <option value="">Sin asignar</option>
                            {catalogs.users.map((u) => (
                                <option key={u.id} value={u.id}>{u.display_name || u.label || `Usuario ${u.id}`}</option>
                            ))}
                        </Select>

                        <Select label="Sponsor" value={form.sponsor_user_id} onChange={handleChange("sponsor_user_id")}>
                            <option value="">Sin asignar</option>
                            {catalogs.users.map((u) => (
                                <option key={u.id} value={u.id}>{u.display_name || u.label || `Usuario ${u.id}`}</option>
                            ))}
                        </Select>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <Input type="date" label="Inicio planeado" value={form.planned_start_date} onChange={handleChange("planned_start_date")} />
                        <Input type="date" label="Fin planeado" value={form.planned_end_date} onChange={handleChange("planned_end_date")} />
                    </div>

                    <div
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 16,
                            marginTop: 8,
                            marginBottom: 10,
                        }}
                    >
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 12 }}>
                            Qué quieres copiar
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
                                gap: 10,
                            }}
                        >
                            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#111827" }}>
                                <input type="checkbox" checked={form.copy_tasks} onChange={handleCheckbox("copy_tasks")} />
                                Tareas
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#111827" }}>
                                <input type="checkbox" checked={form.copy_risks} onChange={handleCheckbox("copy_risks")} />
                                Riesgos
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#111827" }}>
                                <input type="checkbox" checked={form.copy_deliverables} onChange={handleCheckbox("copy_deliverables")} />
                                Entregables
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#111827" }}>
                                <input type="checkbox" checked={form.copy_scope} onChange={handleCheckbox("copy_scope")} />
                                Alcance
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#111827" }}>
                                <input type="checkbox" checked={form.copy_documents} onChange={handleCheckbox("copy_documents")} />
                                Documentos
                            </label>
                            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#111827" }}>
                                <input
                                    type="checkbox"
                                    checked={form.open_after_duplicate}
                                    onChange={handleCheckbox("open_after_duplicate")}
                                />
                                Abrir copia al terminar
                            </label>
                        </div>
                    </div>

                    {loadingCatalogs ? (
                        <div style={{ color: "#6b7280", fontSize: 13, marginTop: 8 }}>Cargando catálogos...</div>
                    ) : null}

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                        <ActionButton
                            onClick={onClose}
                            bg="#fff"
                            color="#111827"
                            border="1px solid #d1d5db"
                            disabled={saving}
                        >
                            Cancelar
                        </ActionButton>
                        <ActionButton onClick={handleSubmit} disabled={saving || loadingCatalogs}>
                            {saving ? "Duplicando..." : "Duplicar proyecto"}
                        </ActionButton>
                    </div>
                </div>
            </div>
        </div>
    );
}
